/**
 * The house rules a guide body has to satisfy before it can be published.
 *
 * There are two of these, deliberately. `GuideHtmlValidator` in mintly-backend-graphql-service
 * checks what the model just produced, so it can hand the violations back and retry. This one
 * checks what is about to be stored, which is a different moment and a different author: an
 * operator editing the HTML by hand never goes near the generator. Two entry points, two checks.
 *
 * **They must agree.** If a rule is added there it belongs here too, and vice versa. The token list
 * below is the same list, and the comment above it says where the original lives. A rule enforced in
 * only one of the two is a rule an operator meets by surprise: their edit is refused for something
 * the generator was happy to emit, or the reverse.
 *
 * This is not a security control. The public site sanitises every stored body at render, and that
 * is what stops injected script; these rules are about a guide looking like the rest of the site.
 */

/**
 * Design tokens a guide body may name.
 *
 * Mirrors `GuideHtmlTokens.java` in mintly-backend-graphql-service, which is where the list is
 * decided and where the prompt reads it from.
 */
export const GUIDE_TOKENS = new Set([
  '--color-text',
  '--color-text-muted',
  '--color-surface',
  '--color-background',
  '--color-border',
  '--color-primary',
  '--color-accent',
  '--color-teal',
  '--color-teal-lt',
  '--color-gold',
  '--color-semantic-success',
  '--color-success-bg',
  '--color-warning-bg',
  // The border colour a warning callout is built from. It was added to GuideHtmlTokens.java and
  // not here, so the generator could emit a callout that this gate then refused at publish: the
  // two-lists-in-two-languages trap, found by running the prompt's own examples through both.
  '--color-amber',
  '--color-red',
  '--color-red-lt',
  '--color-badge-bg',
  '--fs-xs',
  '--fs-sm',
  '--fs-base',
  '--fs-xl',
  '--fs-2xl',
  '--fs-3xl',
  '--space-1',
  '--space-2',
  '--space-3',
  '--space-4',
  '--space-6',
  '--space-8',
  '--space-12',
  '--radius-base',
  '--radius-btn',
  '--radius-card',
  '--radius-modal',
  '--radius-chip',
  '--shadow-sm',
  '--shadow-base',
  '--shadow-md',
  '--font-family-display',
  '--font-family-secondary',
  '--font-family-mono',
  '--font-weight-normal',
  '--font-weight-bold',
]);

/** Video hosts the public site's sanitiser will render. Anything else is dropped there. */
export const GUIDE_IFRAME_HOSTS = [
  'https://www.youtube.com/embed/',
  'https://www.youtube-nocookie.com/embed/',
  'https://player.vimeo.com/video/',
];

const STYLE_ATTR = /style\s*=\s*"([^"]*)"/g;
const VAR_REF = /var\(\s*(--[a-zA-Z0-9-]+)/g;
const HEX_COLOUR = /#[0-9a-fA-F]{3,8}\b/;
const FUNCTIONAL_COLOUR = /\b(rgba?|hsla?)\s*\(/;
/*
 * The value is captured and then tested, not excluded with a lookahead. Written as
 * `font-size\s*:\s*(?!var\()` the `\s*` backtracks over the space, the lookahead sees " var("
 * instead of "var(", and a correct `font-size: var(--fs-sm)` is reported as a raw size. The Java
 * validator had exactly that bug.
 */
const FONT_SIZE = /font-size\s*:\s*([^;"]+)/g;
const TAG = /<\s*(\/?)([a-zA-Z][a-zA-Z0-9]*)([^>]*)>/g;
const ATTR = /([a-zA-Z-]+)\s*=\s*"([^"]*)"/g;

function attributeValue(attributes: string, name: string): string | null {
  ATTR.lastIndex = 0;
  let match = ATTR.exec(attributes);
  while (match !== null) {
    if (match[1].toLowerCase() === name) return match[2];
    match = ATTR.exec(attributes);
  }
  return null;
}

/**
 * Everything wrong with this body, or an empty array.
 *
 * All rules run rather than stopping at the first, so one save reports everything an operator has
 * to fix instead of revealing the next problem after each attempt.
 */
export function checkGuideHtml(html: string, title?: string): string[] {
  const problems: string[] = [];
  if (!html || !html.trim()) return problems;

  /*
   * Sections the page renders for itself.
   *
   * Asking the generator was not enough: a real run ended a guide with a "Frequently Asked
   * Questions" heading carrying the same questions the envelope already held, and opened another
   * with the source document's table of contents. Both would have appeared on the page twice.
   */
  const HEADING = /<h([2-4])\b[^>]*>([\s\S]*?)<\/h\1>/gi;
  HEADING.lastIndex = 0;
  let headingMatch = HEADING.exec(html);
  let firstHeading = true;
  while (headingMatch !== null) {
    const heading = headingMatch[2].replace(/<[^>]*>/g, '').trim();
    const normalised = heading.toLowerCase().replace(/[^a-z ]/g, '').trim();

    if (['table of contents', 'contents', 'in this guide'].includes(normalised)) {
      problems.push(
        `Remove the "${heading}" section. The page builds its own contents list from the headings.`,
      );
    }
    if (normalised.startsWith('frequently asked question') || ['faq', 'faqs'].includes(normalised)) {
      problems.push(
        `Remove the "${heading}" section. The FAQs are rendered by the page from seo.faqs; keeping ` +
          'them here puts them on the page twice.',
      );
    }
    if (['related guides', 'related guide', 'related reading'].includes(normalised)) {
      problems.push(
        `Remove the "${heading}" section. The page renders related guides from ` +
          'relatedResourceSlugs, and listing them here also puts every one of them in the ' +
          'contents as though it were a section of this guide.',
      );
    }
    if (
      firstHeading &&
      title &&
      normalised === title.toLowerCase().replace(/[^a-z ]/g, '').trim()
    ) {
      problems.push(
        'The first heading repeats the guide title, which the page already shows as its h1.',
      );
    }
    firstHeading = false;
    headingMatch = HEADING.exec(html);
  }

  /*
   * A backslash before a quote, anywhere in the body.
   *
   * This file had no rule of the kind at all, while its Java counterpart had one for the attribute
   * case, under a comment in this same file claiming the two are kept identical. That gap is not
   * academic: this function IS the publish gate, so a body carrying the damage passed clean here
   * and went live.
   *
   * It has happened twice. First as `class=\"guide-chart\"`, where the class name is read with the
   * backslash included and every rule keyed on it silently stops matching. Then as
   * `What\'s Included in the Salary?`, printed to readers in a heading and again in the contents
   * list built from it. The second is why the rule is about the character rather than the attribute
   * position: a check for `=\"` could never have seen damage in a text node.
   *
   * Both come from the model escaping its JSON string twice. The generator repairs it now, so this
   * is for what the generator does not touch: a body typed into the editor, and every body already
   * in the database.
   */
  const stray = /\\(["'])/.exec(html);
  if (stray) {
    const at = stray.index;
    const around = html.slice(Math.max(0, at - 40), at + 40).replace(/\s+/g, ' ').trim();
    problems.push(
      `Remove the backslash before ${stray[1]} in "${around}". A backslash before a quote means ` +
        'nothing in HTML: in an attribute the value is read with it included and every class and ' +
        'style stops applying, and in text it is printed to the reader.',
    );
  }

  for (const tag of ['script', 'style', 'link', 'meta', 'base', 'form']) {
    if (new RegExp(`<\\s*${tag}\\b`, 'i').test(html)) {
      problems.push(`Remove the <${tag}> element. It is stripped before the page renders.`);
    }
  }
  if (/\son[a-zA-Z]+\s*=/.test(html)) {
    problems.push('Remove the event handler attributes (onclick and similar).');
  }
  if (/javascript:/i.test(html)) {
    problems.push('Remove the javascript: URL.');
  }

  STYLE_ATTR.lastIndex = 0;
  let style = STYLE_ATTR.exec(html);
  while (style !== null) {
    const declarations = style[1];
    const hex = HEX_COLOUR.exec(declarations);
    if (hex) {
      problems.push(
        `Replace the colour literal ${hex[0]} with a token, for example var(--color-text).`,
      );
    }
    const functional = FUNCTIONAL_COLOUR.exec(declarations);
    if (functional) {
      problems.push(`Replace the ${functional[1]}() colour with a token.`);
    }
    FONT_SIZE.lastIndex = 0;
    let size = FONT_SIZE.exec(declarations);
    while (size !== null) {
      const value = size[1].trim();
      if (!value.startsWith('var(')) {
        problems.push(
          `Replace the raw font size "${value}" with a step from the scale, for example var(--fs-base).`,
        );
      }
      size = FONT_SIZE.exec(declarations);
    }
    VAR_REF.lastIndex = 0;
    let token = VAR_REF.exec(declarations);
    while (token !== null) {
      if (!GUIDE_TOKENS.has(token[1])) {
        problems.push(
          `The token ${token[1]} does not exist. The browser drops the whole declaration, so the ` +
            'styling silently does not appear.',
        );
      }
      token = VAR_REF.exec(declarations);
    }
    style = STYLE_ATTR.exec(html);
  }

  let previous = 0;
  let first = true;
  TAG.lastIndex = 0;
  let tag = TAG.exec(html);
  while (tag !== null) {
    const closing = tag[1] !== '';
    const name = tag[2].toLowerCase();
    const attributes = tag[3];

    if (!closing && /^h[1-6]$/.test(name)) {
      const level = Number(name[1]);
      if (level === 1) {
        problems.push('Remove the <h1>. The page renders the title as its own h1; start at <h2>.');
      } else {
        if (first && level !== 2) {
          problems.push(`The body starts at <${name}>. It must start at <h2>.`);
        }
        if (!first && level > previous + 1) {
          problems.push(`The heading level jumps from h${previous} to ${name}. Do not skip a level.`);
        }
        previous = level;
        first = false;
      }
    }

    if (!closing && name === 'img') {
      const alt = attributeValue(attributes, 'alt');
      if (!alt || !alt.trim()) {
        problems.push('Give every <img> a real alt attribute describing the image.');
      }
    }

    if (!closing && name === 'iframe') {
      const src = attributeValue(attributes, 'src');
      if (!src || !GUIDE_IFRAME_HOSTS.some((host) => src.startsWith(host))) {
        problems.push(
          `The <iframe> src ${src ?? 'is missing'} will not render. Embed video only from ` +
            `${GUIDE_IFRAME_HOSTS.join(', ')}`,
        );
      }
      if (!attributeValue(attributes, 'title')) {
        problems.push('Give every <iframe> a title attribute saying what the video is.');
      }
    }

    tag = TAG.exec(html);
  }

  /*
   * Visible text only, and a dash between digits is a range rather than punctuation.
   *
   * A dash inside a URL is not copy, and reporting it gives an operator a violation they cannot act
   * on without breaking the link. A dash inside a figure is the same kind of false positive: a real
   * salary guide came back with 26 en dashes, nearly all of them inside "27,000-40,000" or
   * "3-5 Years", which is correct typography. Refusing those would block every salary guide over
   * something nobody should have to fix by hand.
   *
   * The first version required a digit immediately either side and missed "3.2 LPA - 9 LPA", where
   * a letter sits before the dash. A range is a digit within a few characters on both sides;
   * "3 years - and counting" still fails, because nothing after the dash is a figure.
   */
  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/([0-9][^–—]{0,8})[–—]([^0-9–—]{0,8}[0-9₹$])/g, '$1 to $2');
  if (text.includes('—') || text.includes('–')) {
    problems.push(
      'Remove the em dashes and en dashes from the prose. A dash inside a numeric range is fine.',
    );
  }

  /*
   * Is the body built out of blocks, or is it a wall of prose with tables in it?
   *
   * This is the rule the generator broke, and the reason it needs a gate rather than a sentence in
   * a prompt. The instructions already say, in as many words, that three or more comparable numbers
   * must become a chart, and that a long read gets tinted bands to break it into visible parts. The
   * first guide published from a real document had four tables of salary figures, eight sections,
   * no chart and no band: sixteen patterns offered, three used.
   *
   * The pattern in what it skipped is the point. Every pattern it used is a literal block of markup
   * it could paste; every pattern it ignored requires deciding that the content calls for it.
   * Guidance does not survive that gap, and a check does.
   *
   * Kept identical to checkComposition in GuideHtmlValidator.java, which is what the generation
   * retry reads. A divergence between these two files has already shipped a broken guide once.
   */
  let comparableRows = 0;
  for (const row of html.match(/<tr\b[\s\S]*?<\/tr>/gi) ?? []) {
    const figures = row.replace(/<[^>]*>/g, ' ').match(/[0-9][0-9,.]*/g) ?? [];
    if (figures.length >= 2) comparableRows += 1;
  }
  if (comparableRows >= 3 && !/guide-chart|guide-donut/.test(html)) {
    problems.push(
      `This guide has ${comparableRows} table rows of comparable figures and no chart. Turn the ` +
        'most important of those tables into a guide-chart (pattern 14) or a guide-donut ' +
        '(pattern 15), keeping the table as well.',
    );
  }

  /*
   * A band is a callout around one idea. A real guide opened one for "Step 1" and never closed it,
   * so the sanitiser closed it at the end of the document and a single border was drawn around
   * seventeen sections — every later step looked as though it lived inside the first.
   */
  for (const band of html.match(/<div\b[^>]*class="[^"]*\bguide-band\b[^"]*"[^>]*>[\s\S]*?<\/div>/gi) ?? []) {
    const inner = (band.match(/<h2\b/gi) ?? []).length;
    if (inner > 1) {
      problems.push(
        `A guide-band contains ${inner} section headings. A band is a callout around one idea; ` +
          'this one is almost certainly an unclosed <div>, which draws a border around the rest ' +
          'of the guide.',
      );
      break;
    }
  }

  const sections = (html.match(/<h2\b/gi) ?? []).length;
  if (sections >= 5 && !html.includes('guide-band')) {
    problems.push(
      `This guide has ${sections} sections and no tinted band, so it reads as one undifferentiated ` +
        'column. Put at least two of the sections inside a guide-band (pattern 12).',
    );
  }

  return problems;
}

/**
 * How many internal links a guide body should carry before it reads as an island.
 *
 * A soft target, not a publish rule: the editor warns below it and the save route does not check
 * it. Links between guides and blogs are how a reader (and a crawler) finds the next page, and a
 * guide with none is a dead end on both counts.
 */
export const MIN_INTERNAL_LINKS = 5;

/** The public sections an internal link has to point into to count. */
const INTERNAL_LINK_PREFIXES = ['/hiring-guides/', '/career-guides/', '/academy-guides/', '/blogs/'];
const MINTLY_HOST = /^(?:https?:)?\/\/(?:www\.)?trymintly\.com(?=[/?#]|$)/i;
const HREF = /<a\b[^>]*?\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/gi;

/**
 * Unique links in the body to other guides and blogs.
 *
 * Counted by path, so the same page linked twice, or once relatively and once absolutely, is one
 * link: the target is about how many pages a reader can move on to, not how many anchors there are.
 * Absolute links count only on trymintly.com or www.trymintly.com.
 */
export function countInternalLinks(html: string): number {
  const paths = new Set<string>();
  for (const match of html.matchAll(HREF)) {
    let href = (match[1] ?? match[2] ?? '').trim();
    if (MINTLY_HOST.test(href)) href = href.replace(MINTLY_HOST, '') || '/';
    else if (!href.startsWith('/') || href.startsWith('//')) continue;
    const path = href.replace(/[?#].*$/, '').replace(/\/+$/, '').toLowerCase();
    // The trailing slash is gone, so a bare hub link (`/blogs`) is not a page and does not count.
    if (INTERNAL_LINK_PREFIXES.some((prefix) => path.startsWith(prefix))) paths.add(path);
  }
  return paths.size;
}

/*
 * Author placeholders that reached readers as text.
 *
 * Source documents mark a call to action as a bracketed label, "[Sign Up on Mintly]" or
 * "[ Prepare for Interview ]", meaning "a button goes here". Live guides printed them verbatim. The
 * public site now turns the known labels into links at render, and the generator does the same, so
 * these are warnings rather than rules: they tell an operator what the page will do with the label
 * before it goes out, and that an unknown one shows as plain words.
 */

/** Sentence punctuation. A bracket holding any of it is prose, an aside or a citation, not a button. */
const SENTENCE_PUNCTUATION = /[.,;:!?"“”()]/;
/** The most words a button label has. "Download the Free Salary Report Now" is already seven. */
const MAX_BUTTON_WORDS = 6;
const BRACKETED = /\[([^[\]\n]{0,80})\](\s*\()?/g;

/** Text entities a body is likely to carry. Enough to compare words, not a full decoder. */
const ENTITIES: Record<string, string> = {
  amp: '&',
  nbsp: ' ',
  quot: '"',
  apos: "'",
  lt: '<',
  gt: '>',
  ndash: '–',
  mdash: '—',
  rsquo: '’',
  lsquo: '‘',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#[0-9]+|[a-z]+);/gi, (whole, name: string) => {
    if (name[0] === '#') {
      const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : whole;
    }
    return ENTITIES[name.toLowerCase()] ?? whole;
  });
}

/** Visible text, with anchors removed whole: a label that is already a link is not a placeholder. */
function textOutsideLinks(htmlOrText: string): string {
  return decodeEntities(
    htmlOrText.replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, ' ').replace(/<[^>]*>/g, ' '),
  );
}

function looksLikeButtonLabel(label: string): boolean {
  if (!/^\p{Lu}/u.test(label)) return false; // "[specific piece]" is a fill-in, "[1]" a citation.
  if (/^[xX]$/.test(label)) return false; // A ticked checkbox glyph.
  if (SENTENCE_PUNCTUATION.test(label)) return false;
  const words = label.split(' ').filter(Boolean);
  return words.length >= 1 && words.length <= MAX_BUTTON_WORDS;
}

/**
 * The distinct bracketed button labels in a body, in the order they first appear, trimmed:
 * "[ Prepare for Interview ]" is reported as "Prepare for Interview".
 *
 * Takes HTML or plain text. Not counted: an empty or ticked checkbox ("[ ]", "[x]"), a lowercase
 * fill-in ("[specific piece]"), anything with sentence punctuation, anything over six words, a
 * markdown link ("[Search Jobs](/jobs)", which is a link in another syntax), and a label that is
 * already the text of an `<a>`.
 */
export function findButtonPlaceholders(htmlOrText: string): string[] {
  if (!htmlOrText) return [];
  const text = textOutsideLinks(htmlOrText);
  const seen = new Map<string, string>();
  for (const match of text.matchAll(BRACKETED)) {
    if (match[2]) continue; // Followed by "(": a markdown link.
    const label = match[1].replace(/\s+/g, ' ').trim();
    if (!label || !looksLikeButtonLabel(label)) continue;
    const key = label.toLowerCase();
    if (!seen.has(key)) seen.set(key, label);
  }
  return [...seen.values()];
}

/**
 * A title reduced to what a reader would call the same words: case, entities, `&` against "and",
 * and the dash family (hyphen, en dash, em dash, with or without spaces) all fold together, and
 * trailing punctuation and leading bullets go.
 */
export function normaliseGuideTitle(text: string): string {
  return decodeEntities(text.replace(/<[^>]*>/g, ' '))
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .replace(/\s*[-‐‑–—]+\s*/g, '-')
    .replace(/\s*&\s*/g, ' and ')
    .replace(/^[\s•*·>]+/, '')
    .replace(/[\s.,;:!?]+$/, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Paragraphs and list items whose whole text is another guide's title, with no link inside.
 *
 * That is the shape of a "Read more guides" list typed as plain text: the reader sees a guide's name
 * and has nothing to click. Returns the matched guides' titles (as the guide spells them), distinct,
 * in body order. `titles` is the published guides; leave the guide being edited out of it.
 */
export function findUnlinkedGuideTitles(html: string, titles: readonly string[]): string[] {
  if (!html || titles.length === 0) return [];
  const byKey = new Map<string, string>();
  for (const title of titles) {
    const key = normaliseGuideTitle(title);
    if (key && !byKey.has(key)) byKey.set(key, title);
  }
  const found = new Map<string, string>();
  for (const block of html.matchAll(/<(p|li)\b[^>]*>([\s\S]*?)<\/\1>/gi)) {
    const inner = block[2];
    if (/<a\b/i.test(inner)) continue;
    const key = normaliseGuideTitle(inner);
    const title = byKey.get(key);
    if (title && !found.has(key)) found.set(key, title);
  }
  return [...found.values()];
}
